// NetworkThemes.jsx
import React, { useEffect, useState } from "react";
import { Sparkles, RefreshCcw, Compass, Gift } from "lucide-react";
import "./NetworkThemes.css";

const STATIC_THEMES = {
  total_seeking: 87,
  total_giving: 72,
  seeking_themes: [
    {
      theme: "Seed & Series A capital",
      count: 28,
      example: "Looking for a lead investor in enterprise SaaS",
    },
    {
      theme: "Enterprise go-to-market",
      count: 22,
      example: "Need intros to Fortune 500 decision-makers",
    },
    {
      theme: "Technical co-founders",
      count: 18,
      example: "ML engineer interested in climate tech",
    },
    {
      theme: "LP & institutional connections",
      count: 14,
      example: "Family offices focused on impact investing",
    },
    {
      theme: "Product-market fit validation",
      count: 12,
      example: "B2B SaaS selling into regulated industries",
    },
    {
      theme: "Cross-border expansion",
      count: 10,
      example: "Expanding from Europe into the US market",
    },
    {
      theme: "Hiring senior talent",
      count: 8,
      example: "VP of Engineering for a Series B startup",
    },
  ],
  giving_themes: [
    {
      theme: "VC & angel network intros",
      count: 25,
      example: "Can connect you to 50+ early-stage funds",
    },
    {
      theme: "Technical architecture advice",
      count: 20,
      example: "15 years scaling distributed systems",
    },
    {
      theme: "LP & family office access",
      count: 16,
      example: "Relationships with European and Asian LPs",
    },
    {
      theme: "Regulatory & compliance guidance",
      count: 12,
      example: "Former SEC and FCA network",
    },
    {
      theme: "Hiring playbooks & referrals",
      count: 10,
      example: "Built teams from 5 to 200 people",
    },
    {
      theme: "Market entry strategy",
      count: 8,
      example: "Launched products in 12 countries",
    },
    {
      theme: "Design & brand mentorship",
      count: 6,
      example: "Led design at a public tech company",
    },
  ],
};

export default function NetworkThemes() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = 0) => {
    setRefreshing(true);
    await new Promise((resolve) => setTimeout(resolve, refresh ? 1200 : 500));
    try {
      setData(STATIC_THEMES);
    } catch {
      setData(null);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(0);
  }, []);

  if (!data) {
    return (
      <div className="glass themesLoading" data-testid="themes-loading">
        <p className="overline">vmeetio matcher · clustering themes…</p>
      </div>
    );
  }

  const noThemes =
    (data.seeking_themes?.length || 0) + (data.giving_themes?.length || 0) ===
    0;

  return (
    <div className="themesRoot" data-testid="themes-section">
      {/* Header */}
      <div className="themesHeader">
        <div>
          <p className="overline themesOverline">
            <Sparkles size={12} /> AI clusters · privacy-safe
          </p>
          <h3 className="themesHeading">
            What the room is seeking{" "}
            <span className="themesHeadingAccent">and giving.</span>
          </h3>
        </div>
        <button
          onClick={() => load(1)}
          disabled={refreshing}
          data-testid="themes-refresh"
          className="themesRefreshBtn"
        >
          <RefreshCcw
            size={12}
            className={refreshing ? "themesSpinning" : ""}
          />{" "}
          Re-cluster
        </button>
      </div>

      {noThemes ? (
        <p className="themesEmpty">
          Themes will appear here once more members fill in their seeking and
          giving.
        </p>
      ) : (
        <div className="themesGrid">
          {/* Seeking column */}
          <div className="glass themesColumn" data-testid="themes-seeking">
            <p className="overline themesColumnOverline">
              <Compass size={12} className="themesColumnIcon" /> Members are
              seeking · {data.total_seeking}
            </p>
            <ul className="themesThemeList">
              {(data.seeking_themes || []).map((t, idx) => (
                <li key={`s-${idx}`} className="themesThemeRow">
                  <span className="themesThemeCount">{t.count}</span>
                  <div className="themesThemeBody">
                    <p className="themesThemeName">{t.theme}</p>
                    {t.example && (
                      <p className="themesThemeExample">e.g. {t.example}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Giving column */}
          <div className="glass themesColumn" data-testid="themes-giving">
            <p className="overline themesColumnOverline">
              <Gift size={12} className="themesColumnIcon" /> Members are giving
              · {data.total_giving}
            </p>
            <ul className="themesThemeList">
              {(data.giving_themes || []).map((t, idx) => (
                <li key={`g-${idx}`} className="themesThemeRow">
                  <span className="themesThemeCount">{t.count}</span>
                  <div className="themesThemeBody">
                    <p className="themesThemeName">{t.theme}</p>
                    {t.example && (
                      <p className="themesThemeExample">e.g. {t.example}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
